import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/Form";
import { useAddresDetails, useUserInfo } from "src/hooks/useForms";
import { FiArrowRight } from "react-icons/fi";
import React, { useEffect, useState } from "react";
import { MdErrorOutline, MdOutlineDone } from "react-icons/md";
import { useQuery } from "@tanstack/react-query";
import Select from "react-select";
import { Label } from "../ui/Label";

import FormTypeSubmitMessage from "./FormTypeSubmitMessage";
import { customStyles } from "src/styles/CustomStylesSelect";
import { useGoingUp } from "src/hooks/useScrollUpAndDown";
import { EndingTypes } from "src/types";
import { BASE_URL, QUOTES } from "src/api.config";
import { useTokenStore } from "src/hooks/useTokenStore";

const formSchema = z.object({
  address: z.string().min(1, "Address field is required"),
  address2: z
    .string()
    .min(1, "Address line 2 is required")
    .optional()
    .or(z.literal("")),
  city: z.string().min(1, "City field is required"),
  state: z.string().min(1, "State field is required"),
  zipCode: z.string().min(1, "Zipcode field is required"),
  country: z.string().min(1, "Country field is required"),
});
type FormSchema = z.infer<typeof formSchema>;
interface FormProps {
  ending: EndingTypes;
  path: any;
}

const FormAddressDetails: React.FC<FormProps> = ({ ending, path }) => {
  const setAddressDetails = useAddresDetails((s) => s.setAddressDetails);
  const userInfo = useUserInfo((s) => s.userInfo);
  const [submitFormResponse, setSubmitFormResponse] = useState<any>();
  const [errorSubmitResponse, setErrorSubmitResponse] = useState<any>("");
  const addressDetails = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      address: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
  });
  const [transition, setTransition] = useState(false);
  const goingUp = useGoingUp((s) => s.goingUp);
  const [countriesArray, setCountriesArray] = useState([]);
  const [showEnding, setShowEnding] = useState(false);
  const tokenStore = useTokenStore((s) => s.token);

  useEffect(() => {
    if (errorSubmitResponse) {
      setTimeout(() => {
        setErrorSubmitResponse("");
      }, 5000);
    }
  }, [errorSubmitResponse]);

  const formData = new FormData();
  formData.append("_method", "POST");
  formData.append("email", userInfo?.email);
  formData.append("first_name", userInfo?.firstName);
  formData.append("last_name", userInfo?.lastName);
  formData.append("phone_number", userInfo?.phoneNumber);

  formData.append("phone_number", userInfo?.phoneNumber);
  formData.append("address", addressDetails?.address);
  formData.append("city_town", addressDetails?.city);
  formData.append("state_region_province", addressDetails?.state);
  formData.append("zip_code", addressDetails?.zipCode);
  formData.append("main_part_no", ending.pricesParts.main_part_no!);
  formData.append(
    "main_part_price",
    ending.pricesParts.main_part_price?.toString()!
  );
  formData.append(
    "fitting_price",
    ending.pricesParts.fitting_price?.toString()!
  );
  formData.append("score", ending.pricesParts.score?.toString()!);
  formData.append("acc_part_no1", ending.pricesParts.acc_part_no1?.toString()!);
  formData.append(
    "acc_price_no1",
    ending.pricesParts.acc_price_no1?.toString()!
  );
  if (ending.pricesParts.acc_part_no2 !== null) {
    formData.append(
      "acc_part_no2",
      ending.pricesParts.acc_part_no2?.toString()!
    );
    formData.append(
      "acc_price_no2",
      ending.pricesParts.acc_price_no2?.toString()!
    );
  }
  if (ending.pricesParts.acc_part_no3 !== null) {
    formData.append("acc_part_no3", ending.pricesParts.acc_part_no3);
    formData.append(
      "acc_price_no3",
      ending.pricesParts.acc_price_no3?.toString()!
    );
  }
  if (ending.pricesParts.acc_part_no4 !== null) {
    formData.append("acc_part_no4", ending.pricesParts.acc_part_no4);
    formData.append(
      "acc_price_no4",
      ending.pricesParts.acc_price_no4?.toString()!
    );
  }
  if (ending.pricesParts.acc_part_no5 !== null) {
    formData.append("acc_part_no5", ending.pricesParts.acc_part_no5);
    formData.append(
      "acc_price_no5",
      ending.pricesParts.acc_price_no5?.toString()!
    );
  }
  if (ending.pricesParts.acc_part_no6 !== null) {
    formData.append("acc_part_no6", ending.pricesParts.acc_part_no6);
    formData.append(
      "acc_price_no6",
      ending.pricesParts.acc_price_no6?.toString()!
    );
  }
  if (ending.pricesParts.acc_part_no7 !== null) {
    formData.append("acc_part_no7", ending.pricesParts.acc_part_no7);
    formData.append(
      "acc_price_no7",
      ending.pricesParts.acc_price_no7?.toString()!
    );
  }
  if (ending.pricesParts.acc_part_no8 !== null) {
    formData.append("acc_part_no8", ending.pricesParts.acc_part_no8);
    formData.append(
      "acc_price_no8",
      ending.pricesParts.acc_price_no8?.toString()!
    );
  }
  if (ending.pricesParts.acc_part_no9 !== null) {
    formData.append(
      "acc_part_no8",
      ending.pricesParts.acc_part_no8?.toString()!
    );
    formData.append(
      "acc_price_no8",
      ending.pricesParts.acc_price_no9?.toString()!
    );
  }
  const createQuote = async () => {
    path.forEach((path: { question_id: string; id: string }) => {
      formData.append(`${path.question_id}`, `${path.id}`);
    });
    const response = await fetch(`${BASE_URL}${QUOTES}`, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + tokenStore,
      },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        setSubmitFormResponse(data?.result);
        if (data.result.mailSendresult === " Mail sent ") {
        }
        if (data.result.error_id === "401") {
          setErrorSubmitResponse(data.result.error_msg);
        }
      })
      .catch((err) => {
        console.log(err);
      });

    return response;
  };

  const onSubmit = async (values: FormSchema) => {
    setAddressDetails(values);
    setShowEnding(true);
    // createQuote();
  };

  useEffect(() => {
    setTimeout(() => {
      setTransition(true);
    }, 100);
  }, []);
  const getCountries = async () => {
    const response = await fetch(
      `https://restcountries.com/v3.1/all?fields=name,flags`
    )
      .then((res) => res.json())

      .catch((err) => {
        console.log(err);
      });
    return response;
  };
  const { data: countries } = useQuery({
    queryKey: ["countries"],
    queryFn: getCountries,
  });
  useEffect(() => {
    const countriesData = countries?.map(
      (option: { flags: { png: string }; name: { common: string } }) => {
        return {
          value: option.name.common,
          label: (
            <div className="flex items-center space-x-4">
              <img
                alt={`${option.name.common}`}
                src={option.flags.png}
                className="h-7 w-10 object-cover"
              />
              <p className="text-[16px]">{option.name.common}</p>
            </div>
          ),
        };
      }
    );
    setCountriesArray(countriesData);
  }, [countries]);

  if (showEnding) {
    return <FormTypeSubmitMessage ending={ending} path={path} />;
  }

  return (
    <div
      className={`transition-all ease-in-out duration-500 no-scrollbar ${
        transition
          ? "transform translate-y-0"
          : goingUp
          ? "-translate-y-full opacity-20"
          : "translate-y-full opacity-20"
      }   overflow-auto items-center justify-center`}
    >
      <div className="  relative  h-full overflow-auto w-full no-scrollbar max-w-3xl mx-auto pt-10 pb-10 ">
        <Form {...addressDetails}>
          <form
            className="flex flex-col w-full"
            onSubmit={addressDetails.handleSubmit(onSubmit)}
          >
            <h1 className="font-sans  text-3xl lg:text-4xl font-medium flex items-center space-x-2 ">
              <h4 className="flex items-center space-x-2">
                <FiArrowRight className="h-5 w-5" />
              </h4>
              <span>Address Details*</span>
            </h1>

            <div className="flex flex-col mt-10 w-full">
              <FormField
                control={addressDetails.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address*</FormLabel>
                    <FormControl>
                      <div className="w-full relative outline-none   h-10 text-sm mt-2">
                        <input
                          type="text"
                          {...field}
                          className="w-full outline-none h-10 text-sm"
                          placeholder="65 Hansen Way"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mt-10" />
              <FormField
                control={addressDetails.control}
                name="address2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address line 2</FormLabel>
                    <FormControl>
                      <div className="w-full relative outline-none   h-10 text-sm mt-2">
                        <input
                          type="text"
                          {...field}
                          className="w-full outline-none   h-10  text-sm"
                          placeholder="Apartment 4"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mt-10" />
              <FormField
                control={addressDetails.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City/Town*</FormLabel>
                    <FormControl>
                      <div className="w-full relative outline-none   h-10 text-sm mt-2">
                        <input
                          type="text"
                          {...field}
                          className="w-full outline-none   h-10  text-sm"
                          placeholder="Palo Alto"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mt-10" />
              <FormField
                control={addressDetails.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State/Region/Province*</FormLabel>
                    <FormControl>
                      <div className="w-full relative outline-none   h-10 text-sm mt-2">
                        <input
                          type="text"
                          {...field}
                          className="w-full outline-none   h-10  text-sm"
                          placeholder="California"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mt-10" />
              <FormField
                control={addressDetails.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zip/Post code*</FormLabel>
                    <FormControl>
                      <div className="w-full relative outline-none h-10 text-sm mt-2">
                        <input
                          type="text"
                          {...field}
                          className="w-full outline-none h-10  text-sm"
                          placeholder="94304"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mt-10" />
              <Controller
                name="country"
                render={({ field }) => (
                  <>
                    <Label className="mb-3">Country</Label>
                    <Select
                      styles={customStyles}
                      options={countriesArray}
                      onChange={(val: any) => {
                        field.onChange(val.value);
                      }}
                    />
                  </>
                )}
              />

              <div className="mt-10" />
              <button
                className="  text-blueish w-24 mr-auto py-2"
                type="submit"
              >
                <p className="font-medium">Submit</p>
              </button>
            </div>
          </form>
        </Form>
        {submitFormResponse?.mailSendresult === " Mail sent " && (
          <div className="flex items-center space-x-2">
            <MdOutlineDone className="h-5 w-5 text-green-500" />
            <h1 className="font-medium text-green-500">Mail sent</h1>
          </div>
        )}
        {errorSubmitResponse !== "" && (
          <div className="flex items-center space-x-2">
            <MdErrorOutline className="h-5 w-5 text-gray-400" />
            <h1 className="font-medium text-gray-400">{errorSubmitResponse}</h1>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormAddressDetails;
